import PrimaryButton from "components/buttons/primary-button";
import Logo from "components/logo";
import LinkText from "components/typography/linkText";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { Spin as Hamburger } from "hamburger-react";
import Typography from "components/typography";
import { set } from "react-datepicker/dist/date_utils";
import { GetUser } from "services/user/getUser";

const Navbar = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { pathname } = useLocation();
  const [activePage, setActivePage] = useState("");
  const user = GetUser();

  useMemo(() => {
    if (pathname.includes("/about-us")) {
      setActivePage("about");
    } else if (pathname.includes("/pricing")) {
      setActivePage("pricing");
    } else if (pathname.includes("/blog")) {
      setActivePage("blog");
    } else {
      setActivePage("");
    }
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (showDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [showDropdown]);

  return (
    <Container scrolled={scrolled ? "true" : "false"}>
      <Logo width="8rem" />
      <NavItems>
        <NavItem>
          <LinkText
            text="About Us"
            to="/about-us"
            color={activePage === "about" ? Colors.yellow500 : Colors.white100}
            target="_self"
            underline={false}
            size="lg"
            hoverColor={Colors.yellow500}
          />
        </NavItem>
        <NavItem>
          <PrimaryButton
            variant="text"
            textColor={Colors.white100}
            target="_self"
            text="Tools"
            textSize="lg"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M11.1866 5.00005L6.99858 9.00005L2.81055 5.00005"
                  stroke="#CDCDCD"
                  stroke-width="1.19658"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            }
            iconPosition="right"
            iconSize="1.4"
          />
        </NavItem>{" "}
        <NavItem>
          <LinkText
            text="Pricing"
            to="/pricing"
            color={
              activePage === "pricing" ? Colors.yellow500 : Colors.white100
            }
            target="_self"
            underline={false}
            size="lg"
            hoverColor={Colors.yellow500}
          />
        </NavItem>
        <NavItem>
          <LinkText
            text="Blog"
            to="/blog"
            color={activePage === "blog" ? Colors.yellow500 : Colors.white100}
            target="_self"
            underline={false}
            size="lg"
            hoverColor={Colors.yellow500}
          />
        </NavItem>
      </NavItems>

      {user ? (
        <DashboardButton>
          <PrimaryButton
            text="Dashboard"
            onClick={() => navigate("/overview")}
            variant="p-fade"
          />
        </DashboardButton>
      ) : (
        <Buttons>
          <PrimaryButton
            variant="text"
            text="Login"
            textColor={Colors.white100}
            target="_self"
            fitContent
            onClick={() => navigate("/login")}
          />
          <PrimaryButton
            text="Join Us"
            sparkle
            inset
            onClick={() => navigate("/create-account")}
          />
        </Buttons>
      )}

      <MobileButtons>
        {user ? (
          <PrimaryButton
            text="Dashboard"
            onClick={() => navigate("/overview")}
            fitContent
            variant="p-fade"
          />
        ) : (
          <PrimaryButton
            variant="text"
            text="Login"
            textColor={Colors.white100}
            target="_self"
            fitContent
            onClick={() => navigate("/login")}
          />
        )}
        <Hamburger
          size={20}
          color={Colors.white100}
          onToggle={(toggled) => {
            if (toggled) {
              setShowDropdown(true);
            } else {
              setShowDropdown(false);
            }
          }}
        />
      </MobileButtons>
      <Dropdown open={showDropdown ? "true" : "false"}>
        <DropNavItems>
          <DropNavItem>
            <LinkText
              text="About Us"
              to="/about-us"
              color={Colors.white100}
              target="_self"
              underline={false}
              size="4xl"
              hoverColor={Colors.yellow500}
            />
          </DropNavItem>
          <DropNavItem>
            <Typography
              color={Colors.white100}
              size="4xl"
              weight="medium"
              mr="1"
            >
              Tools
            </Typography>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15.9814 7.14293L9.99853 12.8572L4.01562 7.14293"
                stroke="#CDCDCD"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </DropNavItem>
          <DropNavItem>
            <LinkText
              text="Pricing"
              to="/pricing"
              color={Colors.white100}
              target="_self"
              underline={false}
              size="4xl"
              hoverColor={Colors.yellow500}
            />
          </DropNavItem>
          <DropNavItem>
            <LinkText
              text="Blog"
              to="/blog"
              color={Colors.white100}
              target="_self"
              underline={false}
              size="4xl"
              hoverColor={Colors.yellow500}
            />
          </DropNavItem>
        </DropNavItems>
        {!user && (
          <>
            <DropdownButton>
              <PrimaryButton
                variant="p-fade"
                text="Login"
                onClick={() => navigate("/login")}
              />
            </DropdownButton>
            <DropdownButton>
              <PrimaryButton
                text="Join Us"
                onClick={() => navigate("/create-account")}
              />
            </DropdownButton>
          </>
        )}
      </Dropdown>
    </Container>
  );
};

export default Navbar;

const Container = styled.nav<{ scrolled: string }>`
  padding: 1.4rem 10.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  top: 0;
  z-index: 10;
  position: relative;
  position: ${(props) => (props.scrolled === "true" ? "fixed" : "sticky")};
  background-color: ${(props) =>
    props.scrolled === "true" ? "rgba(0,0,0,0.5)" : "transparent"};
  transition: all ease-in-out 0.1s;
  backdrop-filter: ${(props) =>
    props.scrolled === "true" ? "blur(10px)" : "unset"};

  @media only screen and (max-width: 769px) {
    padding: 1.2rem;
    width: 100%;
  }
`;

const NavItems = styled.div`
  display: flex;
  align-items: center;
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;

const NavItem = styled.div`
  margin-left: 4.8rem;
`;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: 8rem 10.8rem;
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;
const DashboardButton = styled.div`
  width: fit-content;
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;

const MobileButtons = styled.div`
  display: none;
  gap: 1.6rem;
  align-items: center;
  @media only screen and (max-width: 769px) {
    display: flex;
  }
`;

const Dropdown = styled.div<{ open: string }>`
  display: none;
  width: 100%;
  position: absolute;
  top: 100%;
  left: ${(props) => (props.open === "true" ? "0" : "-100%")};
  height: calc(100vh - 100%);
  background-color: #0d0d0d;
  transition: all ease-in-out 0.1s;
  @media only screen and (max-width: 769px) {
    display: block;
    padding: 3.2rem;
  }
`;

const DropNavItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  margin-bottom: 4rem;
`;
const DropNavItem = styled.div`
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.div`
  width: 100%;
  height: fit-content;
  margin-top: 2rem;
  display: flex;
`;
